import React, { useMemo } from 'react';
import { Box, Image, Preloader, QRCode } from '@common-fe/common-fe';
import styled from 'styled-components';

import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import { ICreateDocumentAggregatorDtoDocumentTypeEnum } from '@/dtos';
import { useGetReceiptQRCodeQuery } from '@/modules/UseMyMoney/queries';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';

import useGenerateQrCode from './hooks/useGenerateQrCode';

const IS_LOGO_HIDDEN = whiteLabelingConfig.title;

const QRCodeImageWrapper = styled(Box)`
  position: absolute;
  width: 100%;
  height: 100%;
`;

interface Props {
  storeKey: string;
  size?: number;
  logoSize?: number;
  imagePadding?: number;
  withPadding?: boolean;
  bgColor?: string;
  documentType?: ICreateDocumentAggregatorDtoDocumentTypeEnum;
}

const UploadFileQRCodeContainer: React.FC<Props> = ({
  storeKey,
  size = 100,
  logoSize = 33,
  withPadding,
  imagePadding = 0,
  bgColor,
  documentType,
}) => {
  const { values: { documentId } } = useFilesInDocumentStore(storeKey);
  const { qrCodeData, isFetching, refetch } = useGetReceiptQRCodeQuery(documentId);
  const { fileUploadUrl, logoUrl } = useGenerateQrCode({
    qrCodeData,
    isFetching,
    onGenerateQrCode: refetch,
    documentType,
  });

  const paddingSize = useMemo(() => (withPadding ? 16 : 0), [withPadding]);

  return (
    <Box
      width={{ min: `${size}px` }}
      height={{ min: `${size}px` }}
      align="center"
      justify="center"
      data-testId="upload-file-qr-code_wrapper"
    >
      {isFetching ? (
        <Preloader testId="qr-code_preloader" />
      ) : (
        <Box {...(withPadding ? { margin: 'spacing8' } : {})} style={{ position: 'relative' }} data-testId="upload-file-qr-code">
          <QRCode
            value={fileUploadUrl}
            level="M"
            size={size - paddingSize}
            bgColor={bgColor}
            {...(IS_LOGO_HIDDEN
              ? {}
              : {
                  imageSettings: {
                    src: '',
                    height: logoSize,
                    width: logoSize,
                    excavate: true,
                  },
                })}
          />
          {!IS_LOGO_HIDDEN && (
            <QRCodeImageWrapper justify="center" align="center">
              <Image
                src={whiteLabelingConfig?.minifyLogo || logoUrl}
                height={logoSize - imagePadding}
                width={logoSize - imagePadding}
                fit="contain"
              />
            </QRCodeImageWrapper>
          )}
        </Box>
      )}
    </Box>
  );
};

export default UploadFileQRCodeContainer;
